/**
 * Drawer -- off-canvas sliding panel used as the mobile nav surface
 * across every SPA (operator app, admin console, vendor sell, buyer
 * shop). Renders into a portal on document.body so the parent
 * grid/flex layout never gets pushed by the drawer's width.
 *
 * Design contract:
 *   * Opens via `transform: translateX(0)`, closes via translateX
 *     of full width. CSS-only animation keeps it cheap.
 *   * Backdrop click + Escape close the drawer.
 *   * `aria-modal=true` + focus shifted to the first interactive
 *     element so screen readers/keyboard users see the trap.
 *   * Body scroll is locked while open so iOS doesn't bounce.
 *
 * Visual tokens (paper background, ink rule, no shadow) match the
 * editorial design system -- no glassmorphism, no float orbs.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cx } from './cx';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Which edge the panel slides in from. Defaults to 'left' (nav drawer). */
  side?: 'left' | 'right';
  /** Tailwind width class for the drawer panel. Default `w-[18rem]`. */
  widthClassName?: string;
  /** Optional aria-label for the drawer dialog. */
  ariaLabel?: string;
}

export function Drawer({
  open,
  onClose,
  children,
  side = 'left',
  widthClassName = 'w-[18rem]',
  ariaLabel = 'Navigation',
}: DrawerProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);

  // ESC to close + body scroll lock while open. Both effects only run
  // when `open` flips, so closed drawers cost nothing.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Shift focus into the panel so keyboard users escape the page-level
    // tab order. First focusable element wins; fall back to the panel.
    const t = window.setTimeout(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = panel.querySelector<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      (focusable ?? panel).focus();
    }, 0);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      window.clearTimeout(t);
    };
  }, [open, onClose]);

  // We always render the portal so the slide-out animation has somewhere
  // to play; visibility is gated by `open` via the transform.
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className={cx(
        'fixed inset-0 z-50 transition-opacity duration-200',
        open ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
      )}
      aria-hidden={!open}
    >
      <button
        type="button"
        aria-label="Close navigation"
        onClick={onClose}
        className="absolute inset-0 bg-ink/30 backdrop-blur-[1px]"
        tabIndex={open ? 0 : -1}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
        className={cx(
          'absolute top-0 bottom-0 bg-paper border-rule outline-none',
          'flex flex-col',
          'transition-transform duration-200 ease-out',
          side === 'left'
            ? cx('left-0 border-r', widthClassName, open ? 'translate-x-0' : '-translate-x-full')
            : cx('right-0 border-l', widthClassName, open ? 'translate-x-0' : 'translate-x-full'),
          'pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]',
        )}
        style={{ maxWidth: '100vw' }}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
